import React, { useEffect, useMemo, useState } from "react";
import ListTotalsFooter from "../../../components/ListTotalsFooter";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Box,
  Button,
  Typography,
  TextField,
  Tooltip,
  Chip,
  Paper,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { FileDownload } from "@mui/icons-material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../../utils/usePersistedState";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";

import { apiData } from "../../../services/api_service";
import { fetchCustomerStatementBalancesAPI } from "../../../config";
import Layout from "../../../layout/Layout";
import { formatAmount, formatNumber, formatAED } from "../../../utils/format";
import ListFilterBar, { emptyListFilters } from "../../../components/ListFilterBar";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

const CustomerStatementList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = usePersistedState("soa-list:viewMode", "customers");
  // Store / Customer / Salesperson / Payment mode / Payment status filters (invoice-wise view)
  const [bar, setBar] = useState({ ...emptyListFilters });
  // Defaults to the current month so the list opens showing something
  // useful instead of every customer's entire outstanding history.
  const dayjsStorage = {
    serialize: (d) => (d ? dayjs(d).format("YYYY-MM-DD") : null),
    deserialize: (v) => (v ? dayjs(v) : null),
  };
  const [fromDate, setFromDate] = usePersistedState("soa-list:fromDate", () => dayjs().startOf("month"), dayjsStorage);
  const [toDate, setToDate] = usePersistedState("soa-list:toDate", () => dayjs().endOf("month"), dayjsStorage);

  // Accepts optional overrides so a "Clear" action can re-fetch with the
  // just-cleared values immediately — reading fromDate/toDate from state
  // right after setFromDate/setToDate would still see the pre-update
  // values (React state updates aren't synchronous), silently re-running
  // the *old* filtered query instead of the cleared one.
  const fetchBalances = async (overrides = {}) => {
    try {
      setLoading(true);
      const effectiveFromDate = "fromDate" in overrides ? overrides.fromDate : fromDate;
      const effectiveToDate = "toDate" in overrides ? overrides.toDate : toDate;
      const payload = {
        fromDate: effectiveFromDate ? dayjs(effectiveFromDate).format("YYYY-MM-DD") : null,
        toDate: effectiveToDate ? dayjs(effectiveToDate).format("YYYY-MM-DD") : null,
        view: viewMode === "invoices" ? "invoices" : "customers",
        store_id: Number(bar.storeId) > 0 ? bar.storeId : undefined,
        customer_id: Number(bar.customerId) > 0 ? bar.customerId : undefined,
        sales_person_id: Number(bar.salesPersonId) > 0 ? bar.salesPersonId : undefined,
        ...(viewMode === "invoices"
          ? {
              payment_method: bar.paymentMode || undefined,
              payment_status: bar.paymentStatus || undefined,
            }
          : {}),
      };
      const res = await apiData("get", fetchCustomerStatementBalancesAPI, payload);
      const list = res?.data?.result || (Array.isArray(res?.data) ? res.data : []);
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch customer balances", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, bar]);

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    fetchBalances({ fromDate: null, toDate: null });
  };

  // Exports exactly the rows currently visible on screen — MRT's own
  // filtered row model, so it already reflects every active column filter
  // (name/phone text, emirate, amount ranges) on top of the server-side
  // From/To Date scope baked into `rows`. Not paginated: every filtered
  // row across every page, same "export everything that matches" contract
  // as the other report pages in this app.
  const handleExportData = (table) => {
    // Pre-pagination model = filtered AND sorted, so the file is in the same
    // order as the screen (getFilteredRowModel ignores sorting).
    const visibleRows = table.getPrePaginationRowModel().rows.map((r) => r.original);
    if (!visibleRows.length) {
      alert("No data to export!");
      return;
    }

    // Amounts go in as real numbers (2-decimal), not formatted text, so
    // Excel can sum / sort them.
    const amt = (v) => Math.round((Number(v) || 0) * 100) / 100;
    const exportData = visibleRows.map((item, index) =>
      viewMode === "invoices"
        ? {
            "#": index + 1,
            "Invoice No.": item.order_number || "",
            "Invoice Date": item.order_date ? dayjs(item.order_date).format("DD-MM-YYYY") : "",
            "Customer / Company": (item.name || "").trim(),
            Phone: item.phone_number || "",
            Email: item.email || "",
            Emirate: item.emirate || "",
            Store: item.store_name || "",
            Salesperson: item.salesperson || "",
            "Invoice Status": item.order_status || "",
            "Payment Method(s)": [...new Set((item.receipts || []).filter((r) => !r.pending).map((r) => r.method).filter(Boolean))].join(", ") || item.payment_methods || "",
            "Payment Type": String(item.payment_types || "")
              .split(",")
              .map((t) => ({ 1: "Credit", 2: "Immediate Payment" }[t.trim()] || t.trim()))
              .filter(Boolean)
              .join(", "),
            "Payment Status": item.payment_statuses || "",
            "Payment Breakdown": (item.receipts || [])
              .map((r, i, arr) => {
                const when = r.date ? ` on ${dayjs(r.date).format("DD-MM-YYYY")}` : "";
                const what = r.pending
                  ? `Pending${when ? ` (due${when})` : ""}`
                  : /advance/i.test(r.source)
                    ? `${r.source}${r.reference ? ` ${r.reference}` : ""}${when}`
                    : `${r.source === "Paid at invoice" ? "Paid at invoice" : r.source}${when}`;
                const prefix = arr.length > 1 ? `${i + 1}) ` : "";
                return `${prefix}AED ${amt(r.amount).toFixed(2)} by ${r.method || "-"} - ${what}`;
              })
              .join("  |  "),
            "No. of Payments": (item.receipts || []).filter((r) => !r.pending).length,
            "Advance Applied (CDN/Proforma)": amt((item.receipts || []).filter((r) => /advance/i.test(r.source)).reduce((s, r) => s + Number(r.amount || 0), 0)),
            "Advance Ref": [...new Set((item.receipts || []).filter((r) => /advance/i.test(r.source)).map((r) => r.reference).filter(Boolean))].join(", "),
            Items: (item.items || [])
              .map((i) => `${i.product_name || i.sku || ""} x${Number(i.quantity) || 0}`)
              .join("; "),
            Subtotal: amt(item.subtotal),
            Discount: amt(item.discount_amount),
            VAT: amt(item.tax_amount),
            "Credit Notes": amt(item.total_credit_notes),
            "Total Invoiced": amt(item.total_invoiced),
            "Total Received": amt(item.total_received),
            "Outstanding Balance": amt(item.outstanding_balance),
          }
        : {
            "#": index + 1,
            "Customer / Company": (item.name || "").trim(),
            Phone: item.phone_number || "",
            Emirate: item.emirate || "",
            "No. of Invoices": Number(item.invoice_count) || 0,
            "Total Invoiced": amt(item.total_invoiced),
            "Total Received": amt(item.total_received),
            "Advance Received (CDN/Proforma)": amt(item.total_cdn_advance),
            "Outstanding (incl. earlier)": amt(item.outstanding_balance),
          }
    );

    // Totals row at the bottom, matching the on-screen footer.
    const total = (k) => amt(visibleRows.reduce((s, r) => s + (Number(r[k]) || 0), 0));
    exportData.push(
      viewMode === "invoices"
        ? {
            "#": "",
            "Invoice No.": `TOTAL (${visibleRows.length} invoices)`,
            Subtotal: total("subtotal"),
            Discount: total("discount_amount"),
            VAT: total("tax_amount"),
            "Credit Notes": total("total_credit_notes"),
            "Total Invoiced": total("total_invoiced"),
            "Total Received": total("total_received"),
            "Outstanding Balance": total("outstanding_balance"),
          }
        : {
            "#": "",
            "Customer / Company": "TOTAL",
            "No. of Invoices": visibleRows.reduce((s, r) => s + (Number(r.invoice_count) || 0), 0),
            "Total Invoiced": total("total_invoiced"),
            "Total Received": total("total_received"),
            "Advance Received (CDN/Proforma)": total("total_cdn_advance"),
            "Outstanding (incl. earlier)": total("outstanding_balance"),
          }
    );

    const ws = XLSX.utils.json_to_sheet(exportData);
    // Show amounts with 2 decimals and thousands separators.
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.t === "n" && c >= range.e.c - (viewMode === "customers" ? 3 : 7)) cell.z = "#,##0.00";
      }
    }
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, viewMode === "invoices" ? "Invoices" : "Statement_of_Account");
    if (viewMode === "invoices") {
      // Second sheet: one row per invoice line item.
      const lines = [];
      visibleRows.forEach((inv) =>
        (inv.items || []).forEach((i) =>
          lines.push({
            "Invoice No.": inv.order_number || "",
            "Invoice Date": inv.order_date ? dayjs(inv.order_date).format("DD-MM-YYYY") : "",
            "Customer / Company": (inv.name || "").trim(),
            Product: i.product_name || "",
            SKU: i.sku || "",
            Qty: Number(i.quantity) || 0,
            "Unit Price": amt(i.selling_price),
            Discount: amt(i.discount_amount),
            "Line Total": amt(i.total_price),
          })
        )
      );
      // Third sheet: one row per payment / advance applied to an invoice.
      const pays = [];
      visibleRows.forEach((inv) =>
        (inv.receipts || []).forEach((r) =>
          pays.push({
            "Invoice No.": inv.order_number || "",
            "Customer / Company": (inv.name || "").trim(),
            "Invoice Total": amt(inv.total_invoiced),
            "Receipt Date": r.date ? dayjs(r.date).format("DD-MM-YYYY") : "",
            Source: r.source,
            "Reference (CDN / Proforma / Txn)": r.reference || "",
            "Payment Method": r.method || "",
            Amount: amt(r.amount),
          })
        )
      );
      if (pays.length) {
        const ws3 = XLSX.utils.json_to_sheet(pays);
        const r3 = XLSX.utils.decode_range(ws3["!ref"]);
        for (let r = 1; r <= r3.e.r; r++) {
          [2, 7].forEach((c) => {
            const cell = ws3[XLSX.utils.encode_cell({ r, c })];
            if (cell && cell.t === "n") cell.z = "#,##0.00";
          });
        }
        fitColumns(ws3);
        XLSX.utils.book_append_sheet(wb, ws3, "Invoice_Payments");
      }
      if (lines.length) {
        const ws2 = XLSX.utils.json_to_sheet(lines);
        fitColumns(ws2);
        XLSX.utils.book_append_sheet(wb, ws2, "Invoice_Items");
      }
    }
    writeXlsxFrozen(XLSX, 
      wb,
      `Statement_of_Account_${viewMode === "invoices" ? "All_Invoices" : "Customer_Wise"}_${dayjs().format("DD_MM_YYYY")}.xlsx`
    );
  };

  // Portfolio KPIs reflect the currently fetched (date-scoped) rows —
  // all-time by default, or the selected From/To window once searched.
  const portfolio = useMemo(
    () => ({
      customers: new Set(rows.map((row) => row.customer_id)).size,
      ...rows.reduce(
        (acc, row) => {
          acc.invoiced += Number(row.total_invoiced) || 0;
          acc.received += Number(row.total_received) || 0;
          acc.advance += Number(row.total_cdn_advance) || 0;
          acc.outstanding += Number(row.outstanding_balance) || 0;
          return acc;
        },
        { invoiced: 0, received: 0, advance: 0, outstanding: 0 }
      ),
    }),
    [rows]
  );

  const emirateOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.emirate).filter(Boolean))).sort(),
    [rows]
  );

  // Column widths follow the data: the wider of the header and the longest
  // value in that column (≈8px per character, plus cell padding and the
  // sort/filter icons in the header), kept within sensible bounds.
  const colWidth = (header, getText, { min = 90, max = 320, extra = 0 } = {}) => {
    const longest = rows.reduce((m, r) => Math.max(m, String(getText(r) ?? "").length), 0);
    // Headers wrap, so only the longest word has to fit.
    const byHeader = Math.max(...header.split(" ").map((w) => w.length)) * 7.4 + 56;
    const byData = longest * 7.4 + 28 + extra;
    return Math.min(max, Math.max(min, Math.ceil(Math.max(byHeader, byData))));
  };

  const columns = useMemo(
    () => [
      ...(viewMode === "invoices"
        ? [
            {
              accessorKey: "order_number",
              header: "Invoice No.",
              size: colWidth("Invoice No.", (r) => r.order_number, { min: 120, max: 220 }),
              filterVariant: "text",
              // The number opens the invoice PDF; clicking elsewhere on the
              // row still opens the customer's statement.
              Cell: ({ row }) =>
                row.original.order_receipt ? (
                  <Link
                    href={row.original.order_receipt}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    fontWeight={600}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.original.order_number}
                  </Link>
                ) : (
                  row.original.order_number
                ),
            },
            {
              accessorKey: "order_date",
              header: "Invoice Date",
              size: colWidth("Invoice Date", () => "00-00-0000", { min: 120, max: 160 }),
              filterVariant: "text",
              Cell: ({ cell }) =>
                cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
            },
          ]
        : []),
      {
        accessorKey: "name",
        header: "Customer / Company",
        size: colWidth("Customer / Company", (r) => (r.name || "").trim(), { min: 150, max: 180 }),
        grow: true,
        filterVariant: "text",
        Cell: ({ row }) => (
          // Long names wrap onto the next line instead of widening the column.
          <Typography
            fontWeight={600}
            color="primary.main"
            sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}
          >
            {row.original.name}
          </Typography>
        ),
      },
      {
        accessorKey: "phone_number",
        header: "Phone",
        size: colWidth("Phone", (r) => r.phone_number, { min: 110, max: 150 }),
        filterVariant: "text",
      },
      {
        accessorKey: "emirate",
        header: "Emirate",
        size: colWidth("Emirate", (r) => r.emirate, { min: 105, max: 150, extra: 18 }),
        filterVariant: "select",
        filterSelectOptions: emirateOptions,
      },
      ...(viewMode === "customers"
        ? [
            {
              accessorKey: "invoice_count",
              header: "No. of Invoices",
              size: colWidth("No. of Invoices", (r) => formatNumber(r.invoice_count), { min: 110 }),
              filterVariant: "text",
              Cell: ({ row }) => formatNumber(row.original.invoice_count),
              muiTableBodyCellProps: { align: "right" },
            },
          ]
        : []),
      {
        accessorKey: "total_invoiced",
        header: "Total Invoiced",
        size: colWidth("Total Invoiced", (r) => formatAED(r.total_invoiced), { min: 110 }),
        filterVariant: "text",
        Cell: ({ cell }) => formatAED(cell.getValue()),
        muiTableBodyCellProps: { align: "right" },
      },
      {
        accessorKey: "total_received",
        header: "Total Received",
        size: colWidth("Total Received", (r) => formatAED(r.total_received), { min: 110 }),
        filterVariant: "text",
        Cell: ({ cell }) => formatAED(cell.getValue()),
        muiTableBodyCellProps: { align: "right" },
      },
      // Advances taken on CDNs / Proformas that no invoice has absorbed yet.
      // They aren't tied to an invoice, so only the customer-wise view has them.
      ...(viewMode === "customers"
        ? [
            {
              accessorKey: "total_cdn_advance",
              header: "Advance Received",
              size: colWidth("Advance Received", (r) => formatAED(r.total_cdn_advance), { min: 110 }),
              filterVariant: "text",
              Cell: ({ cell }) => formatAED(cell.getValue()),
              muiTableBodyCellProps: { align: "right" },
            },
          ]
        : []),
      // {
      //   accessorKey: "total_credit_notes",
      //   header: "Credit Notes",
      //   size: 140,
      //   filterVariant: "range",
      //   Cell: ({ cell }) => formatAED(cell.getValue()),
      //   muiTableBodyCellProps: { align: "right" },
      //   muiTableHeadCellProps: { align: "right" },
      // },
      {
        accessorKey: "outstanding_balance",
        // Cumulative (everything still owed up to the To Date, including
        // earlier months) — unlike the period columns before it.
        header: viewMode === "invoices" ? "Outstanding Balance" : "Outstanding (incl. earlier)",
        size: colWidth(
          viewMode === "invoices" ? "Outstanding Balance" : "Outstanding (incl. earlier)",
          (r) => `${formatAED(r.outstanding_balance)}`,
          { min: 170, extra: 24 }
        ),
        filterVariant: "text",
        Cell: ({ cell }) => {
          const value = Number(cell.getValue()) || 0;
          return (
            <Chip
              label={`${formatAED(value)}`}
              size="small"
              color={value > 0 ? "error" : value < 0 ? "warning" : "success"}
              variant={value === 0 ? "outlined" : "filled"}
              sx={{ fontWeight: 700 }}
            />
          );
        },
        muiTableBodyCellProps: { align: "right" },
      },
    ],
    [emirateOptions, viewMode, rows]
  );

  const KPI_CARDS = [
    { label: "Customers", value: portfolio.customers, prefix: "", icon: GroupsOutlinedIcon, accent: "#64748b" },
    { label: "Total Invoiced (selected dates)", value: portfolio.invoiced, prefix: "AED ", icon: ReceiptLongOutlinedIcon, accent: "#1976d2" },
    { label: "Total Received (selected dates)", value: portfolio.received, prefix: "AED ", icon: PaymentsOutlinedIcon, accent: "#2e7d32" },
    ...(viewMode === "customers"
      ? [{ label: "Advance Received (CDN/Proforma)", value: portfolio.advance, prefix: "AED ", icon: PaymentsOutlinedIcon, accent: "#0288d1" }]
      : []),
    { label: viewMode === "invoices" ? "Total Outstanding" : `Total Outstanding (till ${toDate ? dayjs(toDate).format("DD-MM-YYYY") : "today"})`, value: portfolio.outstanding, prefix: "AED ", icon: ErrorOutlineOutlinedIcon, accent: "#d32f2f", highlight: true },
  ];

  return (
    <Layout>
      {/* Layout's top bar (Header.jsx) is position:fixed with a 70px
          Toolbar and contributes 0 height to normal flow, so every page
          not using the legacy `.employee-form` class (which bakes in 3%
          padding-top) needs its own clearance or the fixed bar covers the
          top of the content — pt: "85px" matches the value already used
          for the same reason in SalesExecDashboard.jsx. */}
      <Box sx={{ pt: "85px", px: { xs: 2, md: 3.5 }, pb: 4 }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Statement of Account
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 0.25 }}>
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Statement of Account</Typography>
            </Breadcrumbs>
          </Box>

          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIosIcon sx={{ fontSize: "14px !important" }} />}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Back
          </Button>
        </Box>

        {/* Per-field search now lives in the table's own column filters
            (name/phone text, emirate select, amount ranges) — this row is
            just the period scope: totals/outstanding below are all-time
            unless a From/To Date is set. */}
        <Grid container spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
          <Grid item xs={6} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="From Date"
                value={fromDate}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => setFromDate(newValue)}
                renderInput={(params) => (
                  <Tooltip title="Include activity from this date (leave blank for all-time)" arrow>
                    <TextField {...params} size="small" fullWidth />
                  </Tooltip>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="To Date"
                value={toDate}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => setToDate(newValue)}
                renderInput={(params) => (
                  <Tooltip title="Include activity up to this date (leave blank for up-to-date)" arrow>
                    <TextField {...params} size="small" fullWidth />
                  </Tooltip>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={fetchBalances}
                disabled={loading}
                sx={{ textTransform: "none", borderRadius: 2, minWidth: 90 }}
              >
                {loading ? "Loading..." : "Search"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
                sx={{ textTransform: "none", borderRadius: 2, minWidth: 80 }}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Invoices, Invoiced and Received cover the selected dates. Outstanding is everything
                still owed up to the To Date, including earlier months.
                {viewMode === "customers"
                  ? " Advance Received is money paid on CDNs / Proformas not yet invoiced; it is already deducted from Outstanding."
                  : " Advances on CDNs / Proformas have no invoice, so they show only in the Customer-wise view."}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                aria-label="Statement of account view"
                onChange={(_, nextMode) => {
                  if (nextMode) setViewMode(nextMode);
                }}
              >
                <ToggleButton value="customers" aria-label="Customer-wise view">
                  Customer-wise
                </ToggleButton>
                <ToggleButton value="invoices" aria-label="All invoices view">
                  All invoices
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Portfolio KPI cards. Plain CSS grid, not MUI's <Grid container>:
          MUI's legacy Grid puts a negative margin on the container for its
          own gutter system, which shrinks how much height that container
          contributes to ITS parent's auto-height — so a taller wrapped
          card can visually render a few px below where the parent thinks
          its own bottom edge is, regardless of what wraps it (confirmed
          via getBoundingClientRect, not a guess). CSS grid + gap avoids
          the whole class of bug. */}
      <Box
        sx={{
          mt: 1.5,
          mb: 2,
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: `repeat(${KPI_CARDS.length}, 1fr)` },
        }}
      >
        {KPI_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <Paper
              key={card.label}
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 3,
                overflow: "hidden",
                borderLeft: `4px solid ${card.accent}`,
                backgroundColor: card.highlight ? "rgba(211,47,47,0.04)" : "#fff",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                <Icon sx={{ fontSize: 16, color: card.accent }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {card.label}
                </Typography>
              </Stack>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: card.highlight ? card.accent : "text.primary" }}>
                {card.prefix}
                {formatAmount(card.value, { decimals: card.label === "Customers" ? 0 : 2 })}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      <ListFilterBar
        value={bar}
        onChange={setBar}
        onClear={() => setBar({ ...emptyListFilters })}
        showStore={false}
        showSalesPerson={false}
        showDates={false}
        showSearch={false}
      />

      <MaterialReactTable
        // Remount on tab switch so column order / filters / sorting from one
        // view don't carry into the other (Invoice No. must lead "All invoices").
        key={viewMode}
        layoutMode="grid"
        columns={columns}
        data={rows}
        state={{ isLoading: loading }}
        enableGlobalFilter={false}
        enableColumnFilters
        enableColumnActions={false}
        enableFullScreenToggle={false}
        enableStickyHeader
        enableRowNumbers={viewMode === "customers"}
        rowNumberMode="original"
        initialState={{
          // All invoices: keep the server order — newest invoice first
          // (order_date DESC, order_id DESC), same as the Invoice list.
          sorting: viewMode === "invoices" ? [] : [{ id: "outstanding_balance", desc: true }],
          pagination: { pageIndex: 0, pageSize: 25 },
          showColumnFilters: true,
        }}
        renderTopToolbarCustomActions={({ table }) => (
          <Tooltip title="Export data to excel sheet" arrow>
            <Button
              onClick={() => handleExportData(table)}
              startIcon={<FileDownload />}
              sx={{ background: "#1a73e829", textTransform: "none" }}
            >
              Export To Excel
            </Button>
          </Tooltip>
        )}
        muiTableHeadCellProps={{
          sx: { "& .Mui-TableHeadCell-Content-Labels": { whiteSpace: "normal", lineHeight: 1.2 } },
        }}
        muiTablePaperProps={{ variant: "outlined", elevation: 0, sx: { borderRadius: 3, overflow: "hidden" } }}
        muiTableBodyRowProps={({ row }) => ({
          // Carry the currently applied From/To Date filter over to the
          // detail page (as ?from=&to=) instead of letting it silently
          // reset to the detail page's own current-month default — that
          // made the two pages disagree about what range was being shown.
          onClick: () => {
            // Always send both — empty means "cleared" (all-time), so the
            // detail page doesn't fall back to its current-month default.
            const params = new URLSearchParams();
            params.set("from", fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "");
            params.set("to", toDate ? dayjs(toDate).format("YYYY-MM-DD") : "");
            navigate(`/reports/customer-statement/${row.original.customer_id}?${params.toString()}`);
          },
          hover: true,
          sx: { cursor: "pointer" },
        })}
        muiTableContainerProps={{
          sx: { maxHeight: "calc(100vh - 300px)", minHeight: 450, overflowY: "auto" },
        }}
        // Header color/weight and row zebra-striping come from the global
        // table styling (tableEnhancements.css) shared by every read-only
        // list/report table in the app — no per-page override needed.
        renderBottomToolbarCustomActions={({ table }) => {
          // Totals reflect what's actually visible after column filters,
          // not the full fetched (date-scoped) set.
          const visible = table.getFilteredRowModel().rows.map((r) => r.original);
          const t = visible.reduce(
            (acc, r) => {
              acc.invoiceCount += Number(r.invoice_count) || 0;
              acc.invoiced += Number(r.total_invoiced) || 0;
              acc.received += Number(r.total_received) || 0;
              acc.advance += Number(r.total_cdn_advance) || 0;
              acc.outstanding += Number(r.outstanding_balance) || 0;
              return acc;
            },
            { invoiceCount: 0, invoiced: 0, received: 0, advance: 0, outstanding: 0 }
          );
          return (
            <ListTotalsFooter
              items={[
                { label: "Total Invoices", value: formatNumber(t.invoiceCount) },
                { label: "Total Invoiced", value: `${formatAED(t.invoiced)}` },
                { label: "Total Received", value: `${formatAED(t.received)}`, tone: "green" },
                ...(viewMode === "customers"
                  ? [{ label: "Advance Received", value: `${formatAED(t.advance)}`, tone: "green" }]
                  : []),
                { label: "Total Outstanding", value: `${formatAED(t.outstanding)}`, tone: "amber" },
              ]}
            />
          );
        }}
      />
      </Box>
    </Layout>
  );
};

export default CustomerStatementList;
